import type React from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import type { MessageCreateRequest } from "@/api/types";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import type { IconComponent } from "@/components/Icon/Icon";
import { Announcement03Icon } from "@/components/Icons/Announcement03Icon";
import { MessageChatSquareIcon } from "@/components/Icons/MessageChatSquareIcon";
import { RadioCard } from "@/components/Radio/RadioCard";
import { FormField } from "@/design-system/FormField";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

import type { AdminCreatePostSharedFormValues } from "../AdminCreatePostPage";

type Value = "readonly" | "withComments";

type AdminCreatePostFormTypeSelectorProps = {
  selectedType: MessageCreateRequest["type"] | undefined;
  allowedTypes: MessageCreateRequest["type"][];
  onSelectType: (type: MessageCreateRequest["type"]) => void;
};

export function AdminCreatePostFormTypeSelector({
  selectedType,
  allowedTypes,
  onSelectType,
}: AdminCreatePostFormTypeSelectorProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<AdminCreatePostSharedFormValues>();
  const sessionUser = useSessionUser();

  useEffect(() => {
    form.register("postType", {
      validate: {
        required: (value) => {
          if (!value || value.length === 0) {
            return t("page.admin-create-post.form.type.error");
          }
        },
      },
    });
  }, [form, t]);

  const postTypeOptions = [
    {
      title:
        sessionUser.project.type === "addressBased"
          ? t("page.admin-create-post.form.type.inform.residents.title")
          : t("page.admin-create-post.form.type.inform.company.title"),
      description: t("page.admin-create-post.form.type.inform.description"),
      icon: <Announcement03Icon />,
      value: "readonly",
      "data-testid": "post-type-announcement-readonly-btn",
      isDisabled: !allowedTypes.includes("announcementReadOnly"),
    },
    {
      title: t("page.admin-create-post.form.type.conversation.title"),
      description: t("page.admin-create-post.form.type.conversation.description"),
      icon: <MessageChatSquareIcon />,
      value: "withComments",
      "data-testid": "post-type-announcement-btn",
      isDisabled: !(allowedTypes.includes("announcement") || allowedTypes.includes("undefined")),
    },
  ] satisfies {
    title: string;
    description: string;
    icon: IconComponent;
    value: Value;
    "data-testid": string;
    isDisabled: boolean;
  }[];

  const handleChangeType = (newType: Value) => {
    if (newType === "readonly") {
      onSelectType("announcementReadOnly");
    } else if (allowedTypes.includes("announcement")) {
      onSelectType("announcement");
    } else {
      onSelectType("undefined");
    }
  };

  function isEnabled(type: Value) {
    if (!selectedType) {
      return false;
    }

    return type === "readonly" ? selectedType === "announcementReadOnly" : selectedType !== "announcementReadOnly";
  }

  return (
    <FormField label={t("page.admin-create-post.section.step-1.title")} hideLabel>
      <FormErrorWrapper<AdminCreatePostSharedFormValues> name="postType" encircle>
        <FormRadioCardGroup>
          {postTypeOptions.map((item) => (
            <RadioCard
              name="postType"
              onChange={() => handleChangeType(item.value)}
              label={item.title}
              description={item.description}
              icon={item.icon}
              value={item.value}
              key={item.value}
              checked={isEnabled(item.value)}
              data-testid={item["data-testid"]}
              disabled={item.isDisabled}
            />
          ))}
        </FormRadioCardGroup>
      </FormErrorWrapper>
    </FormField>
  );
}
